import type { Board } from '../net/api';
import type { GameDef, RoundResult } from '../games/registry';
import { savePlayer, type Player } from '../player';
import { el } from './dom';

export interface ResultOpts {
  game: Pick<GameDef, 'id' | 'title' | 'icon' | 'formatScore' | 'scoreLabel'>;
  result: RoundResult;
  board: Board | null;
  newBest: boolean;
  player: Player;
  /** post now (after naming); returns the board */
  post(): Promise<Board | null>;
  again(): void;
  board_(): void;
  menu(): void;
}

/** the end-of-round sheet, shared by every game */
export function showResult(host: HTMLElement, o: ResultOpts): void {
  const { result, player } = o;
  const standing = el('p', { class: 'standing' });
  const setStanding = (board: Board | null, posted: boolean) => {
    if (result.practice) standing.textContent = 'Practice round — not posted.';
    else if (board?.me) standing.textContent = `#${board.me.rank} of ${board.me.players} on the leaderboard`;
    else if (posted) standing.textContent = 'Offline — your time will post when you are back online.';
    else standing.textContent = '';
  };
  setStanding(o.board, !!player.name);

  const detail = result.detail ?? {};
  const bits = [`${result.mistakes} ${result.mistakes === 1 ? 'mistake' : 'mistakes'}`];
  if (typeof detail.bestStreak === 'number') bits.push(`best streak ${detail.bestStreak}`);

  // Luna turns up when you beat your own time. She was Ed's daughter's dog; the only footage
  // of her that will ever exist lives in lunaloo's reference/. She is here to approve, never
  // to be a target in a game.
  //
  // Practice rounds never qualify: main.ts hard-codes newBest = false for them, deliberately,
  // because Taco Slap's practice mode shows the chant and an easier mode must not set a best.
  // So a practice-only game (Lu or Labu, until its image set stops leaking) gets no Luna.
  const luna = o.newBest
    ? el(
        'figure',
        { class: 'luna' },
        el('img', {
          src: `${import.meta.env.BASE_URL}luna/good-girl.jpg`,
          alt: 'Luna, a tan dog with a black muzzle and a pink collar, sitting and looking up at the camera',
        }),
        el('figcaption', {}, 'Luna approves'),
      )
    : null;

  const sheet = el(
    'div',
    { class: 'sheet result' },
    luna,
    el('h2', {}, result.practice ? 'Practice over' : o.newBest ? '🏆 New personal best!' : 'Round over'),
    el('div', { class: 'score' }, o.game.formatScore(result.score)),
    el('p', { class: 'dim' }, bits.join(' · ')),
    standing,
  );

  if (!player.name && !result.practice) {
    const input = el('input', { type: 'text', maxlength: '24', placeholder: 'your name', autocomplete: 'nickname', 'aria-label': 'your name for the leaderboard' });
    const btn = el('button', { class: 'btn primary', type: 'submit' }, 'Post to leaderboard');
    const form = el('form', { class: 'name-form' }, input, btn);
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const name = input.value.trim();
      if (!name) return;
      player.name = name;
      savePlayer(player);
      btn.disabled = true;
      btn.textContent = 'Posting…';
      const board = await o.post();
      form.remove();
      setStanding(board, true);
    });
    sheet.append(form);
  }

  sheet.append(
    el('button', { class: 'btn primary big', type: 'button', onclick: o.again }, 'Play again'),
    el('div', { class: 'row' }, el('button', { class: 'btn', type: 'button', onclick: o.board_ }, 'Leaderboard'), el('button', { class: 'btn ghost', type: 'button', onclick: o.menu }, 'Menu')),
  );
  host.replaceChildren(sheet);
  host.classList.add('show');
}
